import { motion } from "framer-motion";
import { Compass, Eye, Heart, ShieldCheck, Sparkles, Users } from "lucide-react";
import { MkCTA } from "@/components/marketing/MarketingLayout";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const H1 = ({ children }) => (
  <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1633] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{children}</h1>
);

const VALUES = [
  { icon: Users, title: "Students first, always", copy: "If it doesn't make a student's life better, we don't build it." },
  { icon: ShieldCheck, title: "Honesty over profit", copy: "No commissions, no hidden agendas. Just what genuinely works." },
  { icon: Heart, title: "Belonging beats everything", copy: "Settled students succeed. Lonely ones struggle. We choose belonging." },
  { icon: Sparkles, title: "Small details, big difference", copy: "The right SIM, the right area, the right first week. Details matter." },
];

const TIMELINE = [
  { year: "The beginning", title: "We landed in the UK", copy: "Nervous, overpacked, and knowing nobody. We made every mistake so you don't have to." },
  { year: "The idea", title: "Friends helping friends", copy: "We started answering questions for juniors back home. Then their friends. Then hundreds." },
  { year: "The platform", title: "Castles Creation is born", copy: "City circles, inductions, guides and deals, everything we wished existed, in one place." },
  { year: "Today", title: "Castles AI and beyond", copy: "A personal advisor for every member, and a network growing across every UK student city." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Our story</p>
          <H1>We were the students who figured it out the hard way.</H1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed" data-testid="founder-story">
            Every founder of Castles Creation once stood in an Indian airport with a one-way ticket, an overweight suitcase and a hundred unanswered questions. We landed in the UK excited, and immediately lost. Which SIM? Which bank? Which area? Who do I even ask?
          </p>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            We survived it. Then we started helping the next batch avoid it. Castles Creation is what happens when that promise grows up: a complete platform so no Indian student ever has to feel lost in their first months again.
          </p>
        </motion.div>
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 grid md:grid-cols-2 gap-8">
          <motion.div {...fade()} className="bg-[#0A1633] rounded-3xl p-10 sm:p-12" data-testid="mission-card">
            <Compass className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
            <h2 className="mt-6 text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Our mission</h2>
            <p className="mt-4 text-slate-300 leading-relaxed text-lg">Make every Indian student's move to the UK feel guided, affordable and full of possibility, from the day they get their offer letter.</p>
          </motion.div>
          <motion.div {...fade(0.1)} className="bg-[#FDFDFC] border border-slate-100 rounded-3xl p-10 sm:p-12" data-testid="vision-card">
            <Eye className="w-8 h-8 text-[#10B981]" strokeWidth={1.5} />
            <h2 className="mt-6 text-2xl font-bold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Our vision</h2>
            <p className="mt-4 text-slate-600 leading-relaxed text-lg">A UK where no Indian student ever feels alone, uninformed or taken advantage of. Where landing abroad feels like arriving home.</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <motion.div {...fade()}>
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">What we stand for</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Our values</h2>
        </motion.div>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <motion.div key={v.title} {...fade(i * 0.06)} data-testid={`value-${i}`}
              className="bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
              <v.icon className="w-7 h-7 text-[#10B981]" strokeWidth={1.5} />
              <h3 className="mt-5 text-xl font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{v.title}</h3>
              <p className="mt-2.5 text-slate-600 leading-relaxed">{v.copy}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <motion.div {...fade()}>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">The journey so far</p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>How Castles happened</h2>
          </motion.div>
          <div className="mt-14 space-y-0">
            {TIMELINE.map((t, i) => (
              <motion.div key={t.title} {...fade(i * 0.05)} data-testid={`timeline-${i}`} className="flex gap-6 sm:gap-10">
                <div className="flex flex-col items-center">
                  <div className={`w-4 h-4 rounded-full mt-2 shrink-0 ${i === TIMELINE.length - 1 ? "bg-[#D4AF37]" : "bg-[#10B981]"}`} />
                  {i < TIMELINE.length - 1 && <div className="w-px flex-1 bg-slate-200 my-2" />}
                </div>
                <div className="pb-12">
                  <p className="text-sm font-semibold tracking-[0.15em] uppercase text-slate-400">{t.year}</p>
                  <h3 className="mt-2 text-xl font-semibold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed max-w-xl">{t.copy}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <motion.div {...fade()} className="bg-[#0A1633] rounded-[2.5rem] px-8 sm:px-16 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="promise-heading">Our promise</h2>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            We will always tell you what we'd tell our own younger sibling. Membership stays free. Guidance stays honest. And you'll always have someone in your corner.
          </p>
          <div className="mt-10">
            <MkCTA testid="about-cta" className="!bg-[#D4AF37] !text-[#0A1633]" />
          </div>
        </motion.div>
      </section>
    </main>
  );
}
